import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { createBrowserRouter, Navigate, RouterProvider, useLocation, useParams, type RouteObject } from "react-router";
import { ApiProvider } from "./api/ApiContext";
import type { ApiClient } from "./api/client";
import { ApiError } from "./api/errors";
import { AuthProvider, useAuth } from "./auth/AuthProvider";
import { RequireParty, RequireStaff, SessionLoading, STAFF_HOME } from "./auth/guards";
import { AuthCallbackPage, homeFor, LoginPage, SignupPage } from "./auth/pages";
import { MfaRecoveryPage, PasswordRecoveryPage, RecoverPage } from "./auth/recovery";
import { StepUpProvider } from "./auth/StepUpProvider";
import { ToastProvider } from "./components/ui";
import { AppShell } from "./layout/AppShell";
import { CampaignDetailPage } from "./pages/advertiser/CampaignDetail";
import { CampaignsPage } from "./pages/advertiser/Campaigns";
import { CampaignWizard } from "./pages/advertiser/CampaignWizard";
import { AdvertiserOverview } from "./pages/advertiser/Overview";
import { RefundDetailPage, RefundsPage } from "./pages/advertiser/Refunds";
import { DepositReturnPage, WalletPage } from "./pages/advertiser/Wallet";
import { DebtCaseDetailPage, DebtCasesPage } from "./pages/publisher/DebtCases";
import { EarningsPage } from "./pages/publisher/Earnings";
import { PublisherOverview } from "./pages/publisher/Overview";
import { PayoutDetailPage, PayoutsPage } from "./pages/publisher/Payouts";
import { DestinationReturnPage } from "./pages/publisher/PayoutDestination";
import { ConnectReturnPage, PayoutSetupPage } from "./pages/publisher/PayoutSetup";
import { SiteDetailPage, SiteIntegrationPage } from "./pages/publisher/SiteDetail";
import { AddSitePage, SitesPage } from "./pages/publisher/Sites";
import { DisputeDetailPage, DisputesPage } from "./pages/shared/disputes/Disputes";
import { ApprovalsPage } from "./pages/shared/org/Approvals";
import { AuditLogPage } from "./pages/shared/org/AuditLog";
import { InvitationAcceptPage } from "./pages/shared/org/InvitationAccept";
import { MembersPage } from "./pages/shared/org/Members";
import { NotificationsPage } from "./pages/shared/org/Notifications";
import { CloseAccountPage } from "./pages/shared/CloseAccount";
import { NotFoundPage, OrganizationPage } from "./pages/shared/PendingPages";
import { SecurityPage } from "./pages/shared/SecurityPage";
import { StatementDetailPage, StatementsListPage } from "./pages/shared/StatementsPages";
import { StaffShell } from "./layout/StaffShell";
import { BreakerPage } from "./pages/staff/Breaker";
import { ClientMatrixEntryPage, ClientMatrixPage } from "./pages/staff/ClientMatrix";
import { StaffDebtCasePage, StaffDebtCasesPage } from "./pages/staff/DebtCases";
import { StaffDisputeDetailPage, StaffDisputesPage } from "./pages/staff/Disputes";
import { PromotionsPage } from "./pages/staff/Promotions";
import { ProviderOperationDetailPage, ProviderOperationsPage } from "./pages/staff/ProviderOperations";
import { QuarantineDetailPage, ReconciliationPage } from "./pages/staff/Reconciliation";
import { ReviewItemPage, ReviewQueuePage } from "./pages/staff/Review";
import { SafetyPage } from "./pages/staff/Safety";
import { StaffHome, StaffNotFound } from "./pages/staff/StaffHome";
import { WriteOffDetailPage, WriteOffsPage } from "./pages/staff/WriteOffs";

function RootRedirect() {
  const { state } = useAuth();
  if (state.status === "loading") return <SessionLoading />;
  if (state.status === "signed_out") return <Navigate to="/login" replace />;
  if (state.status === "platform") return <Navigate to={STAFF_HOME} replace />;
  return <Navigate to={homeFor(state.partyType)} replace />;
}

/** Keeps the path segment and query string when forwarding an external return URL into a portal tree. */
function Forward({ to }: { to: (outcome: string) => string }) {
  const { outcome = "" } = useParams();
  const location = useLocation();
  return <Navigate to={to(outcome) + location.search} replace />;
}

const sharedChildren = (): RouteObject[] => [
  { path: "statements", element: <StatementsListPage /> },
  { path: "statements/:statementId", element: <StatementDetailPage /> },
  { path: "disputes", element: <DisputesPage /> },
  { path: "disputes/:disputeId", element: <DisputeDetailPage /> },
  { path: "security", element: <SecurityPage /> },
  { path: "organization", element: <OrganizationPage /> },
  // Organization screens (0062). Notification links are party-agnostic
  // paths like "/org/approvals" and "/org/members" (0062's triggers),
  // resolved under the portal's home, so these paths match them.
  { path: "org", element: <Navigate to="members" replace /> },
  { path: "org/members", element: <MembersPage /> },
  { path: "org/approvals", element: <ApprovalsPage /> },
  { path: "org/audit", element: <AuditLogPage /> },
  { path: "org/notifications", element: <NotificationsPage /> },
  { path: "close-account", element: <CloseAccountPage /> },
  { path: "*", element: <NotFoundPage /> },
];

export const routes: RouteObject[] = [
  { path: "/", element: <RootRedirect /> },
  { path: "/login", element: <LoginPage /> },
  { path: "/signup", element: <SignupPage /> },
  { path: "/recover", element: <RecoverPage /> },
  // Account recovery (cmd/core-platform/recovery.go). The emailed links are
  // /recover/password#token=… and /recover/mfa#token=… (web/README.md).
  { path: "/recover/password", element: <PasswordRecoveryPage /> },
  { path: "/recover/mfa", element: <MfaRecoveryPage /> },
  // Organization invitations (org.go:49). The token is in the fragment:
  // /org/invitations/accept#token=…; the notifier in this build sends
  // /invite#token=… (internal/auth/org_notify.go:79), so both render it.
  { path: "/org/invitations/accept", element: <InvitationAcceptPage /> },
  { path: "/invite", element: <InvitationAcceptPage /> },
  { path: "/auth/callback", element: <AuthCallbackPage /> },
  // External return URLs fixed by core-platform (advertiser.go:126-127, publisher.go:409-410).
  { path: "/wallet/deposits/:outcome", element: <Forward to={(o) => `/advertiser/wallet/deposits/${o}`} /> },
  { path: "/payouts/connect-account/:outcome", element: <Forward to={(o) => `/publisher/payout-setup/${o}`} /> },
  // The payout-destination onboarding link's return/refresh (account_lifecycle.go:140, :161; 0065).
  { path: "/payouts/destination/:outcome", element: <Forward to={(o) => `/publisher/payout-setup/destination/${o}`} /> },
  {
    path: "/advertiser",
    element: <RequireParty partyType="advertiser" />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <AdvertiserOverview /> },
          { path: "campaigns", element: <CampaignsPage /> },
          { path: "campaigns/new", element: <CampaignWizard /> },
          { path: "campaigns/:campaignId", element: <CampaignDetailPage /> },
          { path: "wallet", element: <WalletPage /> },
          { path: "wallet/deposits/:outcome", element: <DepositReturnPage /> },
          { path: "refunds", element: <RefundsPage /> },
          { path: "refunds/:refundId", element: <RefundDetailPage /> },
          ...sharedChildren(),
        ],
      },
    ],
  },
  {
    path: "/publisher",
    element: <RequireParty partyType="publisher" />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <PublisherOverview /> },
          { path: "sites", element: <SitesPage /> },
          { path: "sites/new", element: <AddSitePage /> },
          { path: "sites/:siteId", element: <SiteDetailPage /> },
          { path: "sites/:siteId/integration", element: <SiteIntegrationPage /> },
          { path: "earnings", element: <EarningsPage /> },
          { path: "payouts", element: <PayoutsPage /> },
          { path: "payouts/:payoutId", element: <PayoutDetailPage /> },
          { path: "debt-cases", element: <DebtCasesPage /> },
          { path: "debt-cases/:caseId", element: <DebtCaseDetailPage /> },
          { path: "payout-setup", element: <PayoutSetupPage /> },
          { path: "payout-setup/:outcome", element: <ConnectReturnPage /> },
          { path: "payout-setup/destination/:outcome", element: <DestinationReturnPage /> },
          ...sharedChildren(),
        ],
      },
    ],
  },
  // The staff console (UX-DESIGN.md §5): platform-party accounts with
  // platform_reviewer only (RequireStaff). Defense in depth: every
  // /v1/internal/* route enforces the role on the server.
  {
    path: STAFF_HOME,
    element: <RequireStaff />,
    children: [
      {
        element: <StaffShell />,
        children: [
          { index: true, element: <StaffHome /> },
          { path: "review", element: <ReviewQueuePage /> },
          { path: "review/:kind/:id", element: <ReviewItemPage /> },
          { path: "disputes", element: <StaffDisputesPage /> },
          { path: "disputes/:disputeId", element: <StaffDisputeDetailPage /> },
          { path: "provider-operations", element: <ProviderOperationsPage /> },
          { path: "provider-operations/:operationId", element: <ProviderOperationDetailPage /> },
          { path: "debt-cases", element: <StaffDebtCasesPage /> },
          { path: "debt-cases/:caseId", element: <StaffDebtCasePage /> },
          { path: "write-offs", element: <WriteOffsPage /> },
          { path: "write-offs/:kind/:caseId", element: <WriteOffDetailPage /> },
          { path: "reconciliation", element: <ReconciliationPage /> },
          { path: "reconciliation/:quarantineId", element: <QuarantineDetailPage /> },
          { path: "promotions", element: <PromotionsPage /> },
          { path: "safety", element: <SafetyPage /> },
          { path: "breaker", element: <BreakerPage /> },
          { path: "client-matrix", element: <ClientMatrixPage /> },
          { path: "client-matrix/:entryId", element: <ClientMatrixEntryPage /> },
          { path: "security", element: <SecurityPage /> },
          { path: "*", element: <StaffNotFound /> },
        ],
      },
    ],
  },
  { path: "*", element: <RootRedirect /> },
];

export function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        staleTime: 15_000,
        retry: (count, err) => err instanceof ApiError && err.retryable && count < 2,
      },
      mutations: { retry: false },
    },
  });
}

export function AppProviders({ client, queryClient, children }: { client?: ApiClient; queryClient?: QueryClient; children: ReactNode }) {
  const [qc] = useState(() => queryClient ?? makeQueryClient());
  return (
    <QueryClientProvider client={qc}>
      <ApiProvider client={client}>
        <ToastProvider>
          <AuthProvider>
            <StepUpProvider>{children}</StepUpProvider>
          </AuthProvider>
        </ToastProvider>
      </ApiProvider>
    </QueryClientProvider>
  );
}

const router = createBrowserRouter(routes);

export function App() {
  return (
    <AppProviders>
      <RouterProvider router={router} />
    </AppProviders>
  );
}
